<template>
  <b-container fluid>
    <b-card
      border-variant="danger"
      :header="$t('AddCategory.header')"
      header-bg-variant="danger"
      header-text-variant="white"
      align="left"
    >
      <div>
        <b-tabs content-class="mt-3" v-model="tabIndex">
          <b-tab v-for="(lang, i) in langOptions" :key="i">
            <template v-slot:title>
              <country-flag :country="getLocale(lang)" size="normal" />
            </template>
          </b-tab>
        </b-tabs>
      </div>

      <div v-for="(t, i) in form.title" :key="i">
        <b-row align-v="center" v-if="t.lang === selectedLanguage">
          <b-col>
            <b-form-group :label="$t('AddCategory.title')">
              <b-form-input type="text" v-model="t.value" required></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>
      </div>

      <b-row align-v="center">
        <b-col>
          <b-form-group :label="$t('AddCategory.parent')">
            <b-form-select
              v-model="form.parent"
              :options="parentOptions"
              @change="takeOutAttachedSubcategories"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row align-v="center">
        <b-col>
          <b-form-group :label="$t('AddCategory.placeNumber')">
            <b-form-input type="text" v-model="form.placeNumber"></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="*" sm="6">
          <b-form-group :label="$t('AddCategory.attachedSubcategories')">
            <div v-for="(child, i) in form.children" :key="i">
              <b-list-group v-if="form.children">
                <b-form-row>
                  <b-col>
                    <b-list-group-item variant="info">
                      {{filterLang(child)}}
                      <b-badge
                        variant="danger"
                        class="float-right"
                        @click="deleteSubcategory(child)"
                      >
                        <font-awesome-icon icon="minus" />
                      </b-badge>
                    </b-list-group-item>
                  </b-col>
                </b-form-row>
              </b-list-group>
            </div>
          </b-form-group>
        </b-col>
        <b-col cols="*" sm="6">
          <b-form-group :label="$t('AddCategory.availableSubcategories')">
            <div v-for="(category, i) in availableCategories" :key="i">
              <b-list-group v-if="availableCategories">
                <b-form-row>
                  <b-col>
                    <b-list-group-item variant="warning">
                      {{filterLang(category)}}
                      <b-badge
                        variant="success"
                        class="float-right"
                        @click="addSubcategory(category)"
                      >
                        <font-awesome-icon icon="plus" />
                      </b-badge>
                    </b-list-group-item>
                  </b-col>
                </b-form-row>
              </b-list-group>
            </div>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row align-v="center">
        <b-col>
          <b-form-group :label="$t('AddCategory.status')">
            <b-form-checkbox
              v-model="form.hidden"
              value="true"
              unchecked-value="false"
            >{{ $t('AddCategory.hidden') }}</b-form-checkbox>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row class="mt-3 justify-content-center">
        <b-col>
          <b-button
            variant="secondary"
            type="submit"
            class="float-right"
            @click="$router.push({name: 'EditCategories'})"
          >{{ $t('AddCategory.cancel') }}</b-button>
        </b-col>
        <b-col>
          <b-button
            variant="danger"
            type="submit"
            @click="buildCategoryAndSave"
            :disabled="!form.title.some(t => (t.value !== '') && (t.value !== null))"
          >{{ $t('AddCategory.save') }}</b-button>
        </b-col>
      </b-row>
      <b-row class="mt-3 justify-content-center">
        <b-alert
          :show="error.received"
          variant="danger"
          class="text-center"
          dismissible
        >{{error.msg}}</b-alert>
      </b-row>
    </b-card>
  </b-container>
</template>

<script>
/* CONTROLLER */
import CategoriesService from '@/services/CategoriesService'
import locales from '../i18n/locales'

export default {
  name: 'AddCategory',
  data () {
    return {
      allCategories: [],
      availableCategories: [],
      error: {
        received: false,
        msg: ''
      },
      form: {
        title: [],
        hidden: false,
        children: [],
        parent: null,
        placeNumber: null
      },
      langOptions: [],
      parents: [],
      parentOptions: [
        { value: null, text: this.$i18n.t('AddCategory.parentDropDownNone') }
      ],
      selectedLanguage: this.$i18n.locale,
      tabIndex: 0
    }
  },
  mounted () {
    // redirect if user not logged in
    let smartdineLocalStorage = JSON.parse(
      window.localStorage.getItem('smartdine')
    )
    if (!smartdineLocalStorage.token) this.$router.push({ name: 'Login' })
    // if all good, fetch category
    // build language and title options
    else {
      this.categories()
      this.languageOptions()
      this.titleOptions()
      this.selectedLanguage = this.langOptions[this.tabIndex].value
    }
  },
  methods: {
    async categories () {
      // get all categories
      try {
        const response = await CategoriesService.getCategories()
        this.allCategories = response.data.categories
        // build Available Subcategories and Parents arrays
        this.allCategories.forEach(category => {
          // filter to exclude categories with a parent and child
          if (!category.parent && category.children.length === 0) {
            this.availableCategories.push(category)
          }
          // filter to exclude categories with a parent
          if (!category.parent) {
            this.parents.push(category)
          }
        })
      } catch (error) {
        this.error.received = true
        this.error.msg = error.response.data.error
      }
      // build parentOptions List
      this.parentList()
    },
    parentList () {
      // reset if parentOptions not empty
      if (this.parentOptions.length > 1) {
        this.parentOptions = []
        this.parentOptions.push({
          value: null,
          text: this.$i18n.t('AddCategory.parentDropDownNone')
        })
      }
      // fill parentOptions
      this.parents.forEach(p => {
        this.parentOptions.push({ value: p._id, text: this.filterLang(p) })
      })
    },
    async addSubcategory (subCategory) {
      // if parent is selected, take it out
      if (this.form.parent) {
        this.form.parent = null
      }
      // push availableSubcategory into attached subcategories
      this.form.children.push(subCategory)
      // take out the subcategory from availableSubcategory list
      this.availableCategories = this.availableCategories.filter(
        category => category._id !== subCategory._id
      )
    },
    async deleteSubcategory (subCategory) {
      // take out subcategory from attached list
      this.form.children = this.form.children.filter(
        child => child._id !== subCategory._id
      )
      // put back into availableCategories
      this.availableCategories.push(subCategory)
    },
    async takeOutAttachedSubcategories () {
      // if Subcategories are attached, take them out
      if (this.form.children) {
        this.form.children.forEach(child => this.deleteSubcategory(child))
      }
    },
    buildCategoryAndSave () {
      // build save object (need to clone this.form)
      let category = JSON.parse(JSON.stringify(this.form))
      // filter for only title objects with values
      category.title = this.form.title.filter(t => {
        return t.value !== null && t.value !== ''
      })
      // get children IDs only
      category.children = []
      this.form.children.forEach(c => {
        category.children.push(c._id)
      })

      // make sure this category doesn't already exist
      let categoryExists = false
      category.title.forEach(t => {
        this.allCategories.forEach(c => {
          if (
            c.title.some(act => act.lang === t.lang && act.value === t.value)
          ) {
            categoryExists = true
          }
        })
      })

      // proceed to save if category does NOT exist
      // else, return message to user
      if (!categoryExists) this.saveCategory(category)
      else {
        this.error.received = true
        this.error.msg = this.$i18n.t('AddCategory.categoryExists')
      }
    },
    async saveCategory (category) {
      try {
        const response = await CategoriesService.addCategory(category)
        const savedCategory = response.data.category

        // if subcategories attached, update them
        if (savedCategory.children.length > 0) {
          try {
            savedCategory.children.forEach(child =>
              this.updateSubcategory(child, savedCategory._id)
            )
          } catch (error) {
            console.log(error)
          }
        }
        // if parent selected, update it
        if (savedCategory.parent) {
          // get parent
          try {
            const response = await CategoriesService.getCategory(
              savedCategory.parent
            )
            // add category into parent's children array
            let children = response.data.category.children
            children.push(savedCategory._id)

            // update parent
            try {
              await CategoriesService.updateCategory({
                _id: savedCategory.parent,
                children: children
              })
            } catch (error) {
              console.log(error)
            }
          } catch (error) {
            console.log(error)
          }
        }
        // finally, redirect back to EditCategories
        this.$router.push({ name: 'EditCategories' })
      } catch (error) {
        this.error.received = true
        this.error.msg = error.response.data.error
      }
    },
    async updateSubcategory (category, parent) {
      try {
        await CategoriesService.updateCategory({
          _id: category,
          parent: parent
        })
      } catch (error) {
        console.log(error)
      }
    },
    languageOptions () {
      // get languages from i18n and assign value/text to langOptions
      this.langOptions = Object.keys(this.$i18n.messages).map(l => {
        return { value: l, text: l.toUpperCase() }
      })
    },
    titleOptions () {
      // get languages from i18n and assign value/text to title
      this.form.title = Object.keys(this.$i18n.messages).map(l => {
        return { lang: l, value: null }
      })
    },
    filterLang (category) {
      // filter for selected language
      let title = category.title.filter(t => {
        return t.lang === this.selectedLanguage
      })
      // if title value undefined, filter for and assign a defined value
      return title.length > 0
        ? title[0].value
        : category.title.filter(t => {
          return t.value.length > 0
        })[0].value
    },
    getLocale (lang) {
      return locales.filter(l => l.code === lang.value)[0].flag
    }
  },
  watch: {
    tabIndex () {
      if (this.tabIndex !== -1) {
        this.selectedLanguage = this.langOptions[this.tabIndex].value
        this.parentList()
      }
    }
  }
}
</script>

<style scoped>
.btn {
  margin: 0.1em 0.25em 0.1em 0.25em;
}

.modal-dialog h3 {
  color: #0d47a1;
}
.badge {
  line-height: 1.5em;
}
</style>



// WEBPACK FOOTER //
// src/components/AddCategory.vue